/**
 * Run `fn` over `items` with at most `limit` calls in flight.
 *
 * Resolves to one Promise.allSettled-style result per item, in input order, so one
 * failure never stops the rest and the caller sees exactly which items failed.
 * Used where a batch was sequential only for lack of this: warehouse photo
 * processing (browser) and Shopify image uploads (warehouse worker).
 *
 * @template T, R
 * @param {T[]} items
 * @param {number} limit - maximum calls in flight, a whole number of at least 1
 * @param {(item: T, index: number) => Promise<R>} fn
 * @returns {Promise<PromiseSettledResult<R>[]>}
 */
export async function runPool(items, limit, fn) {
    // A bad limit would start no workers and hand back empty slots instead of results.
    if (!Number.isInteger(limit) || limit < 1) {
        throw new RangeError(`runPool limit must be a whole number of at least 1, got ${limit}`)
    }
    const results = new Array(items.length)
    let next = 0

    async function worker() {
        while (next < items.length) {
            const index = next++
            try {
                results[index] = { status: 'fulfilled', value: await fn(items[index], index) }
            } catch (reason) {
                results[index] = { status: 'rejected', reason }
            }
        }
    }

    const workerCount = Math.min(limit, items.length)
    await Promise.all(Array.from({ length: workerCount }, worker))
    return results
}
